import { useState } from "react";
import {
  Table,
  Dropdown,
  Menu,
  Modal,
  Select,
  Space,
  message,
  Form,
  InputNumber,
  Input,
  Empty,
  Collapse,
} from "antd";
import {
  AppstoreAddOutlined,
  DeleteOutlined,
  EditOutlined,
  FileAddOutlined,
  MoreOutlined,
  RetweetOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  addMenutoOutlet,
  createMenuItem,
  createMenuSection,
  deleteMenu,
  updateMenuItem,
  updateMenuSection,
} from "../../actions/merchant_actions";
import MenuItemForm from "./MenuItemFom";
import MenuFormModal from "./MenuFormModal";
import { useParams } from "react-router-dom";
import { getOutlets } from "../../actions/acc_actions";

const { Panel } = Collapse;

const MenuTable = ({ data, refetchMenus }) => {
  const { accUrn, businessUrn } = useParams();

  const [menuSectionForm] = Form.useForm();
  const safeData = Array.isArray(data) ? data : [];

  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [sectionModalVisible, setSectionModalVisible] = useState(false);
  const [selectedMenuUrn, setSelectedMenuUrn] = useState(null);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [isMenuModalVisible, setIsMenuModalVisible] = useState(false);
  const [editingSection, setEditingSection] = useState(null);

  const { isLoading, data: outletsData } = useQuery({
    queryKey: ["outlets", businessUrn],
    queryFn: () => getOutlets(businessUrn),
    enabled: !!businessUrn,
  });

  const handleEditMenu = (record) => {
    setSelectedMenu(record); // Pass record to modal
    setIsMenuModalVisible(true); // Open the modal
  };

  const handleAddItem = async (item) => {
    const itemSection = item.section;

    const data = {
      ...item,
      section: {
        title: selectedSection.title,
        menuUrn: selectedMenuUrn,
        ...itemSection,
      },
    };

    try {
      if (selectedMenuItem?.urn) {
        await updateMenuItem({
          account_urn: accUrn,
          data,
        });
        message.success("Menu Item updated successfully!");
      } else {
        await createMenuItem({
          account_urn: accUrn,
          data,
        });
        message.success("Menu Item created successfully!");
      }

      refetchMenus();
      setItemModalVisible(false);
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Something went wrong!";
      message.error(msg);
    }
  };

  const handleAddToOutlet = (record) => {
    setSelectedMenuUrn(record.urn);
    setModalVisible(true);
  };

  const handleCreateSection = (record) => {
    setSelectedMenuUrn(record.urn);
    setSectionModalVisible(true);
  };

  const onCreateSection = async () => {
    try {
      const values = await menuSectionForm.validateFields();

      if (editingSection) {
        // Perform update API
        await updateMenuSection({
          account_urn: accUrn,
          menuUrn: selectedMenuUrn,
          data: values,
        });
        message.success("Section updated successfully!");
      } else {
        await createMenuSection({
          account_urn: accUrn,
          data: values,
          menuUrn: selectedMenuUrn,
        });
        message.success("Menu section created successfully!");
      }
      refetchMenus();
      setSectionModalVisible(false);
      setSelectedMenuUrn(null);
      menuSectionForm.resetFields();
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Something went wrong!";
      message.error(msg);
    }
  };

  const handleAssignMenu = async () => {
    if (!selectedOutlet) {
      message.warning("Please select an outlet.");
      return;
    }

    try {
      await addMenutoOutlet({
        account_urn: accUrn,
        outletUrn: selectedOutlet,
        menuUrn: selectedMenuUrn,
      });
      refetchMenus();
      message.success("Menu added to outlet successfully!");
      setModalVisible(false);
      setSelectedMenuUrn(null);
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Something went wrong!";
      message.error(msg);
    }

    setSelectedOutlet(null);
  };

  const handleDeleteMenu = async (record) => {
    try {
      await deleteMenu({
        account_urn: accUrn,
        menuUrn: record.urn,
      });
      refetchMenus();
      message.success("Menu Deleted successfully!");
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Something went wrong!";
      message.error(msg);
    }

    setSelectedOutlet(null);
  };

  const menuColumns = ({ handleAddToOutlet, handleCreateSection }) => [
    {
      title: "Menu Name",
      dataIndex: ["data", "name"],
      key: "name",
    },
    {
      title: "Currency",
      dataIndex: ["data", "currency_code"],
      key: "currency",
    },
    {
      title: "Min Payment",
      dataIndex: ["data", "minimum_payment_amount"],
      key: "minPayment",
    },
    {
      title: "Max Payment",
      dataIndex: ["data", "maximum_payment_amount"],
      key: "maxPayment",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item
              icon={<RetweetOutlined />}
              key="assign"
              onClick={() => handleAddToOutlet(record)}
            >
              Assign to Outlet
            </Menu.Item>
            <Menu.Item
              key="2"
              icon={<FileAddOutlined />}
              onClick={() => {
                handleCreateSection(record);
              }}
            >
              Create Section
            </Menu.Item>
            <Menu.Item
              icon={<EditOutlined />}
              key="edit"
              onClick={() => handleEditMenu(record)}
            >
              Edit
            </Menu.Item>
            <Menu.Item
              icon={<DeleteOutlined />}
              danger
              key="3"
              onClick={() => {
                Modal.confirm({
                  title: "Are you sure you want to delete this menu?",
                  okText: "Yes, Delete",
                  okType: "danger",
                  cancelText: "Cancel",
                  onOk() {
                    handleDeleteMenu(record); // Your delete logic
                  },
                });
              }}
            >
              Delete Menu
            </Menu.Item>
          </Menu>
        );
        return (
          <Dropdown overlay={menu}>
            <MoreOutlined />
          </Dropdown>
        );
      },
    },
  ];

  const productColumns = [
    {
      title: "SKU",
      dataIndex: "sku",
      key: "sku",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Tax %",
      dataIndex: "tax_percent",
      key: "tax",
    },
    {
      title: "Price (incl. tax)",
      dataIndex: "price_inc_tax",
      key: "price",
    },
    {
      key: "settings",
      render: (_, record) => {
        return (
          <Dropdown
            menu={{
              items: [
                {
                  key: "1",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: () => {
                    setSelectedMenuItem(record);
                    setItemModalVisible(true);
                  },
                },
              ],
            }}
          >
            <a onClick={(e) => e.preventDefault()}>
              <MoreOutlined />
            </a>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      <Table
        style={{ marginTop: 20 }}
        columns={menuColumns({
          handleAddToOutlet,
          handleCreateSection,
        })}
        dataSource={safeData}
        rowKey="urn"
        expandable={{
          expandedRowRender: (record) => {
            const sections = (record?.data?.sections || []).sort(
              (a, b) => a.weight - b.weight,
            );
            const allProducts = record?.data?.products || [];

            return (
              <Collapse size="small" accordion>
                {sections.map((section, idx) => {
                  const sectionProducts = (section.products || [])
                    .map((p) => {
                      const product = allProducts.find(
                        (prod) => prod.sku === p.sku,
                      );
                      return product ? { ...product, weight: p.weight } : null;
                    })
                    .filter(Boolean);

                  return (
                    <Panel
                      header={
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <strong>{section.title}</strong>
                            {section.description && (
                              <div style={{ fontSize: 12, color: "#999" }}>
                                {section.description}
                              </div>
                            )}
                          </div>

                          <Dropdown
                            menu={{
                              items: [
                                {
                                  key: "1",
                                  label: "New Item",
                                  icon: <AppstoreAddOutlined />,
                                  onClick: () => {
                                    setSelectedMenuUrn(record?.urn);
                                    setSelectedSection(section);
                                    setItemModalVisible(true);
                                  },
                                },
                                {
                                  key: "2",
                                  label: "Edit",
                                  icon: <EditOutlined />,
                                  onClick: () => {
                                    setEditingSection(section); // store section being edited
                                    menuSectionForm.setFieldsValue(section); // populate form
                                    setSelectedMenuUrn(record?.urn);
                                    setSectionModalVisible(true);
                                  },
                                },
                                {
                                  key: "3",
                                  icon: <DeleteOutlined />,
                                  danger: true,
                                  label: "Delete",
                                  onClick: () => {
                                    Modal.confirm({
                                      title:
                                        "Are you sure you want to delete this menu?",
                                      okText: "Yes, Delete",
                                      okType: "danger",
                                      cancelText: "Cancel",
                                      onOk() {
                                        handleDeleteMenu(record); // Your delete logic
                                      },
                                    });
                                  },
                                },
                              ],
                            }}
                          >
                            <a onClick={(e) => e.preventDefault()}>
                              <MoreOutlined />
                            </a>
                          </Dropdown>
                        </div>
                      }
                      key={idx}
                    >
                      {sectionProducts.length > 0 ? (
                        <Table
                          columns={productColumns}
                          dataSource={sectionProducts}
                          pagination={false}
                          rowKey="sku"
                          size="small"
                        />
                      ) : (
                        <Empty
                          imageStyle={{ height: 60 }}
                          description="No Items"
                        />
                      )}
                    </Panel>
                  );
                })}
              </Collapse>
            );
          },

          rowExpandable: (record) =>
            Array.isArray(record?.data?.sections) &&
            record.data.sections.length > 0,
        }}
      />

      <Modal
        title="Assign Menu to Outlet"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleAssignMenu}
        okText="Confirm"
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Select
            loading={isLoading}
            placeholder="Select an outlet"
            onChange={(value) => setSelectedOutlet(value)}
            style={{ width: "100%" }}
          >
            {outletsData?.data?.body.map((outlet) => (
              <Select.Option key={outlet.urn} value={outlet.urn}>
                {outlet.name}
              </Select.Option>
            ))}
          </Select>
        </Space>
      </Modal>
      <Modal
        title="Create Section"
        open={sectionModalVisible}
        onCancel={() => setSectionModalVisible(false)}
        onOk={onCreateSection}
        okText="Confirm"
      >
        <Form form={menuSectionForm} layout="vertical" name="menuSectionForm">
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please enter section title" }]}
          >
            <Input placeholder="Section Title" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please enter description" }]}
          >
            <Input.TextArea rows={3} placeholder="Section Description" />
          </Form.Item>

          <Form.Item
            name="weight"
            label="Weight"
            rules={[{ required: true, message: "Please enter section weight" }]}
          >
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
        </Form>
      </Modal>
      <MenuItemForm
        visible={itemModalVisible}
        onClose={() => {
          setItemModalVisible(false);
          setSelectedMenuItem(null); // reset on close
        }}
        onSubmit={handleAddItem}
        initialValues={selectedMenuItem}
      />

      <MenuFormModal
        visible={isMenuModalVisible}
        setVisible={setIsMenuModalVisible}
        initialValues={selectedMenu}
        refetchMenus={() => {
          refetchMenus();
        }}
      />
    </>
  );
};

export default MenuTable;
